/**
 * The one place that decides how long a signed-in session lasts.
 *
 * Two modules reach for a Firebase `Auth`: the token provider that publishes
 * the snapshot, and the credential client that signs people in. Both go
 * through here, because a bare `getAuth` would skip the check below
 * and hand its caller the very session that check exists to end.
 *
 * **A session covers the whole browser, and lasts as long as the account's
 * "Stay signed in" choice says** (`SESSION_LIFETIMES`, `sessionLifetime.ts`):
 * 1, 7 or 30 days without a visit, the default being 30 — or 1 day in a
 * browser where "Keep me signed in on this device" was unchecked at sign-in.
 *
 *   * *The whole browser* (#1112): the session is kept where the SDK keeps it
 *     by default — IndexedDB, falling back to `localStorage` — which every tab
 *     on the origin shares. A link opened in a new tab (a text reminder, an
 *     emailed link) lands signed in, and a sign-out in one tab signs out the
 *     rest. It used to be `sessionStorage`, one tab's alone: every new tab
 *     started signed out, and a signed-out tab erased the `/app` gate's hint
 *     under a signed-in one.
 *   * *Days without a visit*: each page that keeps the session, each time a
 *     tab of it comes back into view, and each api call a tab in view makes
 *     (`firebase.ts`) records a visit, and a session whose last visit is older
 *     than the choice is signed out before anyone is handed the `Auth`. A
 *     browser restart ends nothing; why there is no choice that ends with the
 *     browser is `SESSION_LIFETIMES`' doc.
 *
 * Every sign-out here is the SDK's own, which is local: it stops the next
 * person who opens Reflect, not someone who reads the browser's storage before
 * any page of the app has run. Safari deletes the whole store after 7 days of
 * Safari use without a visit, whatever the choice; nothing here works around
 * that.
 *
 * The default store is left alone rather than named with `setPersistence`:
 * the SDK moves a stored user into IndexedDB on every start, so naming
 * `localStorage` would move it back on every page load, and each move
 * deletes the `localStorage` copy that every other tab is listening to.
 *
 * The SDK is imported dynamically, like everything else on this path:
 * firebase must not reach the marketing chunk, and module evaluation must
 * touch no browser API (prerender-safe).
 */
import type { FirebaseApp } from "firebase/app";
import type { Auth } from "firebase/auth";

import { webStorage } from "../browserStorage";
import { firebaseWebConfig } from "../env";
import {
  appliedSessionLifetime,
  readLastVisit,
  recordVisit,
  sessionHasEnded,
} from "./sessionLifetime";

/**
 * The prefix of the SDK's key for a stored user, `firebase:authUser:<apiKey>:<app>`.
 * Read only in `sessionStorage`; see {@link carryTabSession}.
 */
const STORED_USER_KEY_PREFIX = "firebase:authUser:";

/**
 * The Web Lock every tab on the origin takes to end a session. Its name is
 * from the browser-restart check it was first taken for, and stays: a tab
 * still running older code must queue behind the same lock.
 */
const SESSION_CHECK_LOCK = "reflect-restart-check";

/** The two SDK calls the check makes, from the dynamic import. */
interface SessionCheckSdk {
  getAuth: (app: FirebaseApp) => Auth;
  signOut: (auth: Auth) => Promise<void>;
}

/**
 * Whether a session this browser stores, if any, has outlived the choice in
 * force here. Read without the SDK, from stored values, so a page whose
 * session is still good pays nothing for the check.
 *
 * @returns `true` when a stored session is to be signed out.
 */
function storedSessionHasEnded(): boolean {
  return sessionHasEnded({
    lifetime: appliedSessionLifetime(),
    lastVisitAt: readLastVisit(),
    now: Date.now(),
  });
}

/**
 * Record that the check has run and the session, if any, is good, by counting
 * this as a visit — so the next call in this page, or a sign-in made after a
 * sign-out here, is not ended by the same stale visit.
 */
function recordSessionChecked(): void {
  recordVisit(Date.now());
}

/**
 * Hand a session this tab kept in `sessionStorage` — where every sign-in lived
 * before #1112 — to the SDK's start-up, to move into the shared store.
 *
 * Without it, everyone signed in when #1112 shipped would find themselves
 * signed out on their next page load — including on the way back from
 * Stripe's checkout. The SDK never migrates a user out of `sessionStorage` on
 * its own, and `setPersistence` would move it into a store this tab then never
 * listens to, deaf to a sign-out elsewhere. A copy in `localStorage` the SDK's
 * start-up does migrate, into IndexedDB, clearing both copies and listening
 * there as any tab does — so this runs before `getAuth`.
 *
 * Only a tab open when #1112 shipped holds one; once those are gone this finds
 * nothing, and can go.
 */
function carryTabSession(): void {
  const tab = webStorage("session");
  if (tab === null) return;
  try {
    for (let index = 0; index < tab.length; index += 1) {
      const key = tab.key(index);
      if (key === null || !key.startsWith(STORED_USER_KEY_PREFIX)) continue;
      const blob = tab.getItem(key);
      if (blob !== null) webStorage("local")?.setItem(key, blob);
      break;
    }
  } catch {
    // Storage that refuses: the session stays this tab's, and still is one.
  }
}

/**
 * Run the check that ends a session under a Web Lock every tab on the origin
 * shares.
 *
 * The SDK's start-up writes the user it read back to the store once it has
 * reloaded it over the network, so a tab still starting up when another signs
 * the stale session out would put it back — for every tab. Under the lock, two
 * tabs opened together check one after the other, and the second reads the
 * store only once the first is done with it. Where there are no Web Locks
 * (Safari before 15.4) the check runs unlocked, and each tab signs out
 * whatever it settles on: the stale session survives only if another tab's
 * storage poll catches it in the milliseconds between this tab putting it back
 * and signing it out, and a sign-in made in another tab during those moments
 * is signed out with it.
 *
 * @param check - the check.
 * @returns what the check returns.
 */
async function underSessionCheckLock<T>(check: () => Promise<T>): Promise<T> {
  const locks: LockManager | undefined =
    typeof navigator === "undefined" ? undefined : navigator.locks;
  if (locks === undefined) return check();
  return locks.request(SESSION_CHECK_LOCK, check);
}

/**
 * Sign out a stored session that has outlived the choice in force, then
 * record the check.
 *
 * @param app - the app whose `Auth` to check.
 * @param sdk - `getAuth` and `signOut` from the dynamically imported SDK.
 * @returns the checked `Auth`, or `null` when another tab finished the check
 *   before this one's SDK started.
 */
async function signOutEndedSession(app: FirebaseApp, sdk: SessionCheckSdk): Promise<Auth | null> {
  // Re-read under the lock, before this tab's SDK has read the store: a tab
  // that waited here for another's check finds it done.
  if (!storedSessionHasEnded()) return null;
  const auth = sdk.getAuth(app);
  await auth.authStateReady();
  if (auth.currentUser !== null) await sdk.signOut(auth);
  // Last, so a sign-out that throws leaves the next call to try again rather
  // than waving the stale session through.
  recordSessionChecked();
  return auth;
}

/**
 * Resolve the app's `Auth`, scoped to the browser and to the "Stay signed in"
 * choice in force there ({@link appliedSessionLifetime}).
 *
 * A session that has outlived the choice is signed out *before* the handle is
 * handed out, so no caller reads it. Calling this on every path — rather than
 * memoising — costs three stored values while the session is good, keeps the
 * no-memoised-failure rule the token provider documents, and is how a tab
 * coming back into view is checked (`firebase.ts`).
 *
 * @returns the initialized `Auth`.
 */
export async function sessionScopedAuth(): Promise<Auth> {
  const [{ getApps, getApp, initializeApp }, { getAuth, signOut }] = await Promise.all([
    import("firebase/app"),
    import("firebase/auth"),
  ]);
  // getApps() guard: HMR / double-mount must reuse the existing app.
  const firstInThisPage = getApps().length === 0;
  const app = firstInThisPage ? initializeApp(firebaseWebConfig()) : getApp();
  // Once per page, before `getAuth`, whose start-up is what reads the stores.
  if (firstInThisPage) carryTabSession();
  const checked = storedSessionHasEnded()
    ? await underSessionCheckLock(() => signOutEndedSession(app, { getAuth, signOut }))
    : null;
  // A good session, or one another tab has just checked: this page is a visit.
  if (checked === null) recordSessionChecked();
  return checked ?? getAuth(app);
}
